
import { defineComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

import RetryChecksMutation from '@/apollo/mutations/repository/runs/retryChecks.gql'

import RepoDetailMixin from '~/mixins/repoDetailMixin'
import RoleAccessMixin from '~/mixins/roleAccessMixin'
import RouteQueryMixin from '~/mixins/routeQueryMixin'
import RunDetailMixin from '~/mixins/runDetailMixin'

import { FilterParams } from '~/components/Run/AnalyzerRun.vue'
import { RunDetailActions, RunDetailMutations } from '~/store/run/detail'
import { RunListActions, RunListModuleState } from '~/store/run/list'
import { MetricType } from '~/types/metric'
import { AppFeatures, RepoPerms } from '~/types/permTypes'
import { RunTypes } from '~/types/run'
import {
  Analyzer,
  AnalyzerAnalyzerType,
  Check,
  CheckStatus,
  IgnoreCheckIssueActionChoice,
  Maybe,
  Pr,
  RepositoryKindChoices,
  RepositoryMetricValue,
  Run
} from '~/types/types'
import { NonNullableProperties } from '~/types/utils'

import type { TransformedIssueNode } from '~/store/run/detail'
import {
  CheckFailedReason,
  IssueSeverityFilter,
  IssueTypeOptions,
  issueSeverityFilterToGraphQL,
  issueTypeOptionsToGraphQL
} from '~/types/issues'
import { resolveNodes } from '~/utils/array'
import { UiIssueLevel } from '~/utils/issue'
import { getRunProps } from '~/utils/runs'
import { formatRepoName, safeRenderBackticks, shortenLargeNumber } from '~/utils/string'
import { checkStatusIcon, checkStatusIconColor, getIssueTypeIcon } from '~/utils/ui'

import type { GraphqlMutationResponse } from '~/types/apollo-graphql-types'

/**
 * Page that provides detailed information about generated issues for a specific analyzer run.
 */
export default defineComponent({
  name: 'AnalyzerDetails',
  middleware: [
    async function ({ store, route, redirect, error }) {
      const { provider, owner, repo, runId, analyzer } = route.params

      const runResponse = (await store.dispatch(`run/detail/${RunDetailActions.FETCH_RUN}`, {
        provider,
        owner,
        name: repo,
        runId,
        isRunner: false
      })) as Run | undefined

      // Show the UI state corresponding to archived runs after checking against the error message
      if (
        store.state.run.detail.error.message?.replace('GraphQL error: ', '') ===
        RunTypes.ARCHIVED_RUN
      ) {
        return
      }

      if (!runResponse) {
        // Show `404` page if the run doesn't exist
        error({ statusCode: 404, message: 'This page is not real' })
        return
      }

      const checks = resolveNodes<Check>(store.state.run.detail.run.checks)
      const runAnalyzers = checks
        .map((check) => (check.analyzer ? check.analyzer.shortcode : ''))
        .filter(Boolean)

      if (runAnalyzers.length && !runAnalyzers.includes(analyzer)) {
        redirect(302, ['', provider, owner, repo, 'run', runId, runAnalyzers[0]].join('/'))
      }
    }
  ],
  layout: 'repository',
  mixins: [RepoDetailMixin, RoleAccessMixin, RunDetailMixin, RouteQueryMixin],
  scrollToTop: true,
  /**
   * Function that returns `head` property to configure page metadata.
   */
  head(): Record<string, string> {
    const { owner, repo } = this.$route.params
    let title = ''
    if (this.run.branchName) {
      title = `${this.run.branchName} ${title}`
    }
    if (this.run.commitOid) {
      title = `${title} @ ${this.run.commitOid?.slice(0, 7)} `
    }
    return {
      title: `${title || 'Run'} • ${owner}/${formatRepoName(repo)}`,
      description:
        'DeepSource automates code reviews so you ship to production faster — catching security issues, improving code quality, and helping your team write better code.'
    }
  },
  data() {
    return {
      activeTabIndex: 0,
      autofixLoading: false,
      isScrolled: false,
      showConfirmDialog: false,
      isUpgradeAccountModalOpen: false,
      suppressingMetric: false,
      metricToSuppress: {} as RepositoryMetricValue,
      isLoading: false,
      showAutofixModal: false,
      loadingMore: false,
      loadingFilters: false,
      hasMoreOccurrences: true,
      // Layout state (merged from parent)
      flashActiveAnalyzer: false,
      analyzersInRetryList: [] as string[],
      // Pagination state - cursor-based for allIssues API
      endCursor: null as string | null,
      totalCount: 0,
      // Track ignored issue IDs for greying out in the UI
      ignoredIssueIds: [] as string[],
      // Track which issue has its ignore menu open (for coordination)
      activeIgnoreMenuId: null as string | null
    }
  },
  /**
   * Fetch hook for the page.
   */
  async fetch(): Promise<void> {
    this.isLoading = true

    // Fetch branch runs if check is skipped (for the run selector)
    if (this.currentCheck?.status === CheckStatus.Skip) {
      await this.fetchRuns()
    }

    // Ensure currentCheck exists before proceeding
    if (this.currentCheck?.id) {
      try {
        // Parallelize all independent fetches — perms, check details, and issues
        await Promise.all([
          this.fetchRepoPerms(this.baseRouteParams),
          this.fetchCheck({ checkId: this.currentCheck.id }),
          this.fetchAllOccurrences()
        ])
      } catch (error) {
        // Handle fetch errors gracefully
        this.$logErrorAndToast(error as Error, 'Failed to fetch check details.')
      }
    } else {
      await this.fetchRepoPerms(this.baseRouteParams)
    }

    this.isLoading = false
  },
  /**
   * Mounted lifecycle hook for the page. Binds event bus and socket event listeners.
   */
  mounted(): void {
    this.$root.$on('refetchCheck', this.refetchCheck)
    this.$socket.$on('repo-analysis-updated', this.refetchRunAndCheck)
    this.$socket.$on('autofixrun-fixes-ready', this.refetchRunAndCheck)
    this.handleResize()
    this.handleScroll()
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('scroll', this.handleScroll)
  },

  /**
   * BeforeDestroy lifecycle hook for the page. Unbinds event bus and socket event listeners.
   */
  beforeDestroy(): void {
    this.$root.$off('refetchCheck', this.refetchCheck)
    this.$socket.$off('repo-analysis-updated', this.refetchRunAndCheck)
    this.$socket.$off('autofixrun-fixes-ready', this.refetchRunAndCheck)
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('scroll', this.handleScroll)
  },

  /**
   * Created hook to set initial loading state
   */
  created(): void {
    setTimeout(() => {
      if (this.$fetchState.pending) this.isLoading = true
    }, 300)
  },
  watch: {
    /**
     * Auto-expand Issues section when issues become available
     * (e.g., when run transitions from pending → success/fail via websocket)
     */
    checkHasIssues(hasIssues: boolean): void {
      if (hasIssues) {
        const collapsible = this.$refs.issuesCollapsible as { isCollapsed: boolean } | undefined
        if (collapsible) {
          collapsible.isCollapsed = false
        }
      }
    },
    '$route.params.runId': {
      async handler(newRunId: string, oldRunId: string): Promise<void> {
        if (newRunId && oldRunId && newRunId !== oldRunId) {
          this.isLoading = true
          this.$store.commit(`run/detail/${RunDetailMutations.CLEAR_ALL_ISSUES}`)
          this.endCursor = null
          this.ignoredIssueIds = []

          try {
            // Fetch run first to get new check IDs, then parallelize check details + issues
            await this.fetchCurrentRun(true)
            const newCheckId = this.currentCheck?.id
            if (newCheckId) {
              await Promise.all([
                this.fetchCheck({ checkId: newCheckId, refetch: true }),
                this.fetchAllOccurrences()
              ])
            }
          } finally {
            this.isLoading = false
          }
        }
      }
    },
    '$route.params.analyzer': {
      async handler(newAnalyzer: string, oldAnalyzer: string): Promise<void> {
        if (newAnalyzer && oldAnalyzer && newAnalyzer !== oldAnalyzer) {
          this.isLoading = true
          this.$store.commit(`run/detail/${RunDetailMutations.CLEAR_ALL_ISSUES}`)
          this.endCursor = null
          this.ignoredIssueIds = []

          try {
            // Parallelize check details + issues fetch for new analyzer
            const newCheckId = this.currentCheck?.id
            if (newCheckId) {
              await Promise.all([
                this.fetchCheck({ checkId: newCheckId, refetch: true }),
                this.fetchAllOccurrences()
              ])
            }
          } finally {
            this.isLoading = false
          }
        }
      }
    }
  },
  computed: {
    ...(mapState('run/list', {
      branchRunList: (state: any) => state.branchRunList
    }) as {
      branchRunList: () => RunListModuleState['branchRunList']
    }),
    CheckStatus() {
      return CheckStatus
    },
    CheckFailedReason() {
      return CheckFailedReason
    },
    AnalyzerAnalyzerType() {
      return AnalyzerAnalyzerType
    },
    IgnoreCheckIssueActionChoice() {
      return IgnoreCheckIssueActionChoice
    },
    ignoreContext(): 'pr-check' | 'default-branch-check' {
      return this.run.isForDefaultBranch ? 'default-branch-check' : 'pr-check'
    },
    showAutofixBtn() {
      if (!this.check?.run) return false
      return (
        this.allowAutofix &&
        this.canCreateAutofix &&
        !this.check.run.isForDefaultBranch &&
        !this.check.run.isForCrossRepoPr &&
        Array.isArray(this.check.autofixableIssues) &&
        this.check.autofixableIssues.length
      )
    },
    // Override check from mixin to provide fallback when fetchCheck fails
    check(): Check {
      // Use the detailed check from store if available, otherwise use currentCheck
      const storeCheck = this.$store.state.run.detail.check
      return storeCheck?.id ? storeCheck : (this.currentCheck ?? ({} as Check))
    },
    currentCheck(): Check {
      const filteredCheck = this.checks.filter((edge) => {
        return edge.analyzer?.shortcode === this.$route.params.analyzer
      })

      return filteredCheck[0]
    },
    showMetricNamespace(): boolean {
      return this.$route.params.analyzer === 'test-coverage'
    },
    checkStats() {
      return [
        {
          label: 'resolved',
          value: shortenLargeNumber(this.currentCheck?.issuesResolvedCount ?? 0) as number,
          type: 'juniper'
        },
        {
          label: 'introduced',
          value: shortenLargeNumber(this.currentCheck?.issuesRaisedCount ?? 0) as number,
          type: 'cherry'
        },
        {
          label: 'suppressed',
          value: shortenLargeNumber(this.currentCheck?.issuesSuppressedCount ?? 0) as number,
          type: 'amber'
        }
      ].filter((stat) => stat.value > 0)
    },
    metricsStats() {
      if (!this.check?.metricsCaptured || !Array.isArray(this.check.metricsCaptured)) {
        return []
      }

      const returnArr = []

      const passingMetricsCount = this.check.metricsCaptured.filter(
        (metric) => metric?.isPassing === true
      ).length

      const failingMetricsCount = this.check.metricsCaptured.filter(
        (metric) => metric?.isPassing === false && metric?.isSuppressed !== true
      ).length

      const suppressedMetricsCount = this.check.metricsCaptured.filter(
        (metric) => metric?.isPassing === false && metric?.isSuppressed === true
      ).length

      if (passingMetricsCount > 0) {
        returnArr.push({
          label: 'passing threshold',
          value: shortenLargeNumber(passingMetricsCount),
          type: 'juniper'
        })
      }

      if (failingMetricsCount > 0) {
        returnArr.push({
          label: 'failing threshold',
          value: shortenLargeNumber(failingMetricsCount),
          type: 'cherry'
        })
      }

      if (suppressedMetricsCount > 0) {
        returnArr.push({
          label: 'suppressed',
          value: shortenLargeNumber(suppressedMetricsCount),
          type: 'amber'
        })
      }

      return returnArr
    },
    allowAutofix(): boolean {
      const { provider } = this.$route.params
      return this.$gateKeeper.provider(AppFeatures.AUTOFIX, provider)
    },

    canCreateAutofix(): boolean {
      return this.$gateKeeper.repo(RepoPerms.CREATE_AUTOFIXES, this.repoPerms.permission)
    },

    areMetricsAlerting(): boolean {
      return (
        this.check?.metricsCaptured?.some(
          (metricValue) => metricValue?.isPassing === false && metricValue?.isSuppressed !== true
        ) ?? false
      )
    },

    areMetricsFailing(): boolean {
      return Boolean(
        this.check?.metricsCaptured?.some((metricValue) => metricValue?.isPassing === false)
      )
    },

    metricIconColor(): string {
      return this.areMetricsAlerting ? 'cherry' : this.areMetricsFailing ? 'vanilla-400' : 'juniper'
    },

    metricIconTooltip(): string {
      return this.areMetricsAlerting
        ? 'Metrics are alerting'
        : this.areMetricsFailing
          ? ''
          : 'Metrics are passing'
    },
    canSuppressMetric(): boolean {
      return Boolean(this.repository?.userPermissionMeta?.can_ignore_failing_metrics)
    },
    tooltipText(): string {
      return `Compared to ${this.repository.defaultBranchName}`
    },
    isFilterApplied(): boolean {
      return Boolean(this.queryParams.q || this.queryParams.category || this.queryParams.severity)
    },
    checkHasIssues(): boolean {
      return (this.currentCheck?.issuesRaisedCount ?? 0) > 0
    },
    /**
     * Returns true when we expect issues but they haven't loaded yet.
     * Used to prevent "No issues found" flash during loading transitions.
     */
    isAwaitingIssues(): boolean {
      return this.checkHasIssues && this.allOccurrences.length === 0
    },
    emptyStateTitle(): string {
      if (this.checkHasIssues && this.isFilterApplied) {
        return `No results found for ${
          this.queryParams.q ? `'${this.queryParams.q}'` : 'selected filters'
        }`
      }
      return 'No issues found'
    },
    emptyStateImage(): { webp: string; png: string } {
      if (this.checkHasIssues && this.isFilterApplied) {
        return {
          webp: require('~/assets/images/ui-states/directory/empty-search.webp'),
          png: require('~/assets/images/ui-states/directory/empty-search.gif')
        }
      }
      return {
        webp: require('~/assets/images/ui-states/issues/no-issues-found-static-140px.webp'),
        png: require('~/assets/images/ui-states/issues/no-issues-found-static-140px.png')
      }
    },
    checkFailureReason(): CheckFailedReason {
      return this.check?.errorsRendered?.length > 0
        ? CheckFailedReason.Error
        : this.areMetricsFailing
          ? CheckFailedReason.MetricsFailing
          : CheckFailedReason.Issues
    },
    UiIssueLevel() {
      return UiIssueLevel
    },
    isSubrepo(): boolean {
      return this.repository.kind === RepositoryKindChoices.Subrepo
    },
    repoPath(): string {
      return this.repository.path ?? ''
    },
    /**
     * Whether issues can be ignored
     */
    canIgnoreIssues(): boolean {
      return this.repoPerms.canIgnoreIssues
    },
    allOccurrences(): TransformedIssueNode[] {
      return this.$store.state.run.detail.allIssuesNodes
    },
    /**
     * Whether the current list contains any AI review issues
     */
    hasAiReviews(): boolean {
      return this.allOccurrences.some((issue) => issue.isAiReview)
    },
    // Layout computed properties (merged from parent)
    canRetryAnalysis(): boolean {
      return this.$gateKeeper.repo(RepoPerms.RETRY_ANALYSIS, this.repoPerms.permission)
    },
    retryableChecks(): Check[] {
      return this.checks.filter((check) => check.isRetryable)
    },
    analyzersInRetry: {
      get(): Set<string> {
        return new Set(this.analyzersInRetryList)
      },
      set(value: Set<string>) {
        this.analyzersInRetryList = Array.from(value)
      }
    },
    showArchivedRunEmptyState(): boolean {
      // @ts-expect-error: runDetailError will have a message
      return this.runDetailError.message?.replace('GraphQL error: ', '') === RunTypes.ARCHIVED_RUN
    },
    /**
     * Get the run nodes for all other runs on this branch
     */
    branchRuns(): Run[] {
      if (this.run.branchName) {
        const resolvedRuns = resolveNodes(this.branchRunList[this.run.branchName])
        return resolvedRuns.filter((run) => run.runId !== this.run.runId)
      }
      return []
    },
    /**
     * Breadcrumb links for the layout wrapper
     */
    breadcrumbLinks(): string[] {
      const runProps = getRunProps(this.run)
      return [
        runProps.isForDefaultBranch
          ? this.run.commitMessage || runProps.commitOid.substring(0, 7)
          : this.run.pullRequest?.title || this.run.pullRequestNumberDisplay
      ].filter(Boolean) as string[]
    },
    /**
     * Whether the current check has inferred artifacts (test-coverage only)
     */
    checkHasInferredArtifacts(): boolean {
      if (this.currentCheck?.analyzer?.shortcode !== 'test-coverage') {
        return false
      }
      return this.currentCheck?.hasInferredArtifacts ?? false
    },
    /**
     * The PR from which artifacts were inferred (test-coverage only)
     */
    checkInferredArtifactsPr(): Pr | null {
      if (this.currentCheck?.analyzer?.shortcode !== 'test-coverage') {
        return null
      }
      return this.currentCheck?.inferredArtifactsPr ?? null
    }
  },
  methods: {
    getIssueTypeIcon,
    safeRenderBackticks,
    shortenLargeNumber,
    checkStatusIcon,
    checkStatusIconColor,
    ...mapActions('run/list', [RunListActions.FETCH_BRANCH_RUNS_LIST]),
    ...mapActions('run/detail', {
      ignoreMetric: RunDetailActions.IGNORE_CHECK_METRIC,
      fetchAllIssues: RunDetailActions.FETCH_ALL_ISSUES
    }),
    hasStatusMessage(
      status: CheckStatus
    ): status is
      | CheckStatus.Pass
      | CheckStatus.Pend
      | CheckStatus.Cncl
      | CheckStatus.Timo
      | CheckStatus.Wait
      | CheckStatus.Atmo {
      return [
        CheckStatus.Pass,
        CheckStatus.Pend,
        CheckStatus.Cncl,
        CheckStatus.Timo,
        CheckStatus.Wait,
        CheckStatus.Atmo
      ].includes(status)
    },
    getStatusMessage(status: CheckStatus, analyzer?: Maybe<Analyzer>): string {
      const messages = {
        [CheckStatus.Pass]: 'No issues found',
        [CheckStatus.Pend]: 'Analysis in progress',
        [CheckStatus.Cncl]: 'Check was cancelled',
        [CheckStatus.Timo]: 'Check timed out',
        [CheckStatus.Wait]: `Check is waiting for ${
          analyzer?.analyzerType === AnalyzerAnalyzerType.Community
            ? 'SARIF artifacts'
            : 'artifacts'
        }`,
        [CheckStatus.Atmo]: "Analysis artifacts didn't arrive in time"
      }

      return this.hasStatusMessage(status) ? messages[status] : ''
    },
    isValidCheck(value: Check): value is NonNullableProperties<Check> {
      return Boolean(value.analyzer)
    },
    isValidFilter(value: unknown): value is FilterParams {
      const { q, category, sort } = value as FilterParams
      return typeof q === 'string' && typeof category === 'string' && typeof sort === 'string'
    },
    /**
     * Scroll handler that sets isScrolled to true if the window's scroll position exceeds a certain threshold
     */
    handleScroll(): void {
      this.isScrolled = process.client && window.scrollY > 20
    },

    isMetricAggregate(metric: RepositoryMetricValue): boolean {
      return metric.namespace.key === MetricType.aggregate
    },

    /**
     * Network only refetch of details of a check and its issues.
     */
    async refetchCheck(checkId: string): Promise<void> {
      if (this.currentCheck?.id === checkId) {
        this.$store.commit(`run/detail/${RunDetailMutations.CLEAR_ALL_ISSUES}`)
        this.endCursor = null
        this.ignoredIssueIds = []
        await this.fetchCheck({ checkId: this.currentCheck.id, refetch: true })
        await this.fetchAllOccurrences()
      }
    },

    /**
     * Network only refetch of details of a run, specified check and its issues.
     */
    async refetchRunAndCheck({
      run_id,
      check_id
    }: {
      run_id: string
      check_id?: string
    }): Promise<void> {
      if (this.$route.params.runId === run_id) {
        if (check_id) {
          await Promise.all([this.fetchCurrentRun(true), this.refetchCheck(check_id)])
        } else {
          await this.fetchCurrentRun(true)
          if (this.currentCheck?.id) {
            await this.refetchCheck(this.currentCheck.id)
          }
        }
      }
    },

    /**
     * Handle issue ignored event from IssueEditor - update local state without refetching
     */
    handleIssueIgnoredLocally({
      ignoredIssueIds,
      checkId
    }: {
      ignoredIssueIds: string[]
      checkId: string
    }): void {
      const count = ignoredIssueIds.length

      // Add to local ignored list for greying out
      this.ignoredIssueIds = [...this.ignoredIssueIds, ...ignoredIssueIds]

      // Update counts in Vuex store (decrement raised, increment suppressed)
      this.$store.commit(`run/detail/${RunDetailMutations.UPDATE_CHECK_ISSUE_COUNTS}`, {
        checkId,
        decrementRaised: count,
        incrementSuppressed: count
      })

      // Decrement totalCount badge to reflect suppressed issues
      this.totalCount = Math.max(0, this.totalCount - count)

      // Refetch autofixable issues to reflect suppression changes
      this.fetchAutofixableIssues({ checkId, refetch: true })
    },

    /**
     * Handle ignore menu toggle events from IssueEditor - coordinate to close other menus
     */
    handleIgnoreMenuToggled({ id, isOpen }: { id: string; isOpen: boolean }): void {
      this.activeIgnoreMenuId = isOpen ? id : null
    },

    /**
     * Sets the index for the currently active tab
     */
    updateActiveTabIndex(index: number): void {
      this.activeTabIndex = index
    },

    /**
     * Ensures that the Issues tab is displayed when resizing from a tablet or smaller viewport back to a larger size
     */
    handleResize(): void {
      if (process.client && window.innerWidth > 768) this.updateActiveTabIndex(0)
    },

    /**
     * Route query callback - handles filter/search changes
     */
    async refetchAfterRouteChange(): Promise<void> {
      this.loadingFilters = true
      this.$store.commit(`run/detail/${RunDetailMutations.CLEAR_ALL_ISSUES}`)
      this.endCursor = null
      this.ignoredIssueIds = []
      await this.fetchAllOccurrences()
      this.loadingFilters = false
    },

    /**
     * Fetch of details of the current page's run.
     */
    fetchCurrentRun(refetch = false): Promise<Run | undefined> {
      const { runId, repo, owner, provider } = this.$route.params
      return this.fetchRun({
        provider,
        owner,
        name: repo,
        runId,
        isRunner: false,
        refetch
      })
    },

    /**
     * Fetch all runs on this branch
     */
    async fetchRuns(refetch = true): Promise<void> {
      if (this.run.branchName) {
        await this.fetchBranchRuns({
          ...this.baseRouteParams,
          branchName: this.run.branchName,
          limit: 30,
          refetch
        })
      }
    },

    /**
     * Fetch issue occurrences for the flat list view with cursor-based pagination
     */
    async fetchAllOccurrences(refetch = true): Promise<void> {
      // Prefer currentCheck (from fresh run data) over store check (may be stale)
      const checkId = this.currentCheck?.id || this.check?.id
      if (!checkId) {
        this.$logErrorAndToast(new Error('No checkId available'), 'Failed to fetch issues.')
        return
      }

      try {
        // Map UI filter values to GraphQL enum values
        const categoryEnum = this.queryParams.category
          ? issueTypeOptionsToGraphQL[this.queryParams.category as IssueTypeOptions]
          : undefined
        const severityEnum = this.queryParams.severity
          ? issueSeverityFilterToGraphQL[this.queryParams.severity as IssueSeverityFilter]
          : undefined

        await this.fetchAllIssues({
          checkId,
          limit: 15,
          after: refetch ? undefined : this.endCursor,
          q: (this.queryParams.q as string) || undefined,
          category: categoryEnum,
          severity: severityEnum,
          refetch
        })

        // Update pagination state from the fetched data
        const storeAllIssues = this.$store.state.run.detail.allIssues
        this.totalCount = storeAllIssues?.totalCount ?? 0
        this.endCursor = storeAllIssues?.pageInfo?.endCursor ?? null

        // Update hasMoreOccurrences flag based on pageInfo
        this.hasMoreOccurrences = storeAllIssues?.pageInfo?.hasNextPage ?? false
      } catch (error) {
        this.$logErrorAndToast(error as Error, 'Failed to fetch issues.')
      }
    },

    /**
     * Load more occurrences for infinite scroll (cursor-based pagination)
     */
    async loadMoreOccurrences(): Promise<void> {
      if (this.loadingMore || !this.hasMoreOccurrences || !this.endCursor) {
        return
      }

      this.loadingMore = true

      try {
        await this.fetchAllOccurrences(false) // false = append mode using endCursor
      } catch (error) {
        this.$logErrorAndToast(error as Error, 'Failed to load more issues.')
      } finally {
        this.loadingMore = false
      }
    },

    /**
     * Update search in queryParams, but remove filter for empty string
     */
    updateSearch(val: string): void {
      this.endCursor = null // Reset pagination on search
      if (val) {
        this.addFilters({ q: val })
      } else {
        this.removeFilter('q')
      }
    },

    /**
     * Update severity filter in queryParams
     */
    updateSeverityFilter(val: string): void {
      this.endCursor = null // Reset pagination on filter change
      if (val) {
        this.addFilters({ severity: val })
      } else {
        this.removeFilter('severity')
      }
    },

    /**
     * Creates a list of analyzers that contributed to the given metric's aggregate calculation
     */
    getAggregateContributingChecks(metricShortCode: string) {
      return this.check?.metricsCaptured
        ? [...this.check.metricsCaptured]
            .filter(
              (metric) =>
                metric?.namespace.key !== MetricType.aggregate &&
                metric?.shortcode === metricShortCode
            )
            .map((metric) => metric?.namespace)
        : []
    },

    /**
     * Creates autofixes for selected issues and redirects user to the created autofix run.
     */
    async autofixSelectedIssues(
      selectedIssueShortcodeArray: Array<string>,
      close?: () => void
    ): Promise<void> {
      const { owner, provider, repo } = this.$route.params
      this.autofixLoading = true
      try {
        const response = await this.createAutofixPullRequest({
          input: {
            issues: selectedIssueShortcodeArray,
            checkId: this.check.id
          }
        })
        if (response?.autofixRunId) {
          this.$router.push(`/${provider}/${owner}/${repo}/autofix/${response.autofixRunId}`)
          close?.()
          this.showAutofixModal = false
        }
      } catch (e) {
        const err = e as Error
        if (err.message.includes('Your autofix run quota has been exhausted')) {
          this.isUpgradeAccountModalOpen = true
        } else {
          this.$toast.danger('An error occurred while creating autofixes for selected issues.')
        }
      } finally {
        this.autofixLoading = false
      }
    },

    /**
     * Gets the icon data for a given metric namespace.
     */
    getIconData(metricNamespace: { analyzer_logo: any; analyzer_shortcode: any }): {
      analyzerLogo: string
      shortcode: string
      name: string
    } {
      return {
        analyzerLogo: metricNamespace.analyzer_logo,
        shortcode: metricNamespace.analyzer_shortcode,
        name: metricNamespace.analyzer_shortcode
      }
    },

    confirmMetricSuppression(metricsCaptured: RepositoryMetricValue) {
      if (metricsCaptured) {
        this.metricToSuppress = metricsCaptured
        this.showConfirmDialog = true
      }
    },

    async suppressMetric(close: () => void) {
      this.suppressingMetric = true
      try {
        const checkId = this.check?.id || this.currentCheck?.id
        if (!checkId) {
          this.$toast.danger('An error occurred while suppressing the metric.')
          return
        }

        if (this.metricToSuppress.shortcode) {
          const response = await this.ignoreMetric({
            checkId,
            metricShortcode: this.metricToSuppress.shortcode as string,
            key: this.metricToSuppress.namespace.key
          })

          if (response) {
            await this.refetchRunAndCheck({ run_id: this.run.runId, check_id: checkId })
            close?.()
          }
        } else {
          this.$toast.danger('An error occurred while suppressing the metric.')
        }
      } finally {
        this.suppressingMetric = false
      }
    },

    /**
     * Retry given checks (merged from parent)
     */
    async retryChecks(analyzerShortcodes: string[]): Promise<boolean> {
      const analyzersToRetry = analyzerShortcodes.filter(
        (shortcode) => !this.analyzersInRetry.has(shortcode)
      )
      this.analyzersInRetry = new Set([...Array.from(this.analyzersInRetry), ...analyzerShortcodes])
      try {
        if (analyzersToRetry.length) {
          const response: GraphqlMutationResponse = await this.$applyGraphqlMutation(
            RetryChecksMutation,
            {
              input: { runId: this.$route.params.runId, analyzers: analyzersToRetry }
            }
          )
          if (response.data.retryChecksInRun?.ok) {
            if (analyzerShortcodes.includes(this.$route.params.analyzer)) {
              this.refetchRunAndCheck({ run_id: this.$route.params.runId })
            }
            return true
          }
        }
      } catch (e) {
        const err = e as Error
        this.$logErrorAndToast(err, err.message.replace('GraphQL error: ', '') as `${string}.`)
        this.analyzersInRetry = new Set(
          this.analyzersInRetryList.filter((analyzer) => !analyzerShortcodes.includes(analyzer))
        )
      }
      return false
    },

    /**
     * Sets `flashActiveAnalyzer` to true for 1 second, and then resets it back to false
     */
    triggerActiveAnalyzerFlash() {
      this.flashActiveAnalyzer = true
      setTimeout(() => {
        this.flashActiveAnalyzer = false
      }, 1000)
    }
  }
})
